/**
 * Route rendering (spec v0.2 §4.3).
 *
 * The line is drawn from a per-segment GeoJSON so that colouring by exposure is
 * a paint change rather than a rewrite when M2 lands. Until then every segment
 * carries `sun: 0` and the whole route renders in the shaded colour — which is
 * honest: with no shade data, nothing is claimed to be sunny.
 */

import type { Map as MlMap, LngLatLike } from 'maplibre-gl'
import maplibregl from 'maplibre-gl'

const SRC = 'hladom-route'
const HERE = 'hladom-here'
const LAYER_HERE = 'hladom-here-dot'
const LAYER_CASING = 'hladom-route-casing'
const LAYER_LINE = 'hladom-route-line'

export interface Endpoint {
  lngLat: [number, number]
  marker: maplibregl.Marker
}

function pin(color: string, label: string): HTMLElement {
  const el = document.createElement('div')
  el.className = 'pin'
  el.style.setProperty('--pin', color)
  el.textContent = label
  return el
}

export function makeMarker(
  map: MlMap,
  lngLat: [number, number],
  kind: 'from' | 'to',
  onDragEnd: (next: [number, number]) => void,
): Endpoint {
  const marker = new maplibregl.Marker({
    element: pin(kind === 'from' ? '#0e7490' : '#f59e0b', kind === 'from' ? 'A' : 'B'),
    draggable: true,
    anchor: 'bottom',
  })
    .setLngLat(lngLat as LngLatLike)
    .addTo(map)

  marker.on('dragend', () => {
    const { lng, lat } = marker.getLngLat()
    onDragEnd([lng, lat])
  })

  return { lngLat, marker }
}

/**
 * One feature per segment, carrying that segment's sun exposure.
 *
 * `walked` dims the part already behind you, so the line doubles as a progress
 * bar without needing a second layer.
 */
function toSegments(
  line: GeoJSON.LineString,
  sun: number[] = [],
  walkedUpTo = -1,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  const c = line.coordinates
  for (let i = 0; i < c.length - 1; i++) {
    features.push({
      type: 'Feature',
      properties: { sun: sun[i] ?? 0, walked: i < walkedUpTo ? 1 : 0 },
      geometry: { type: 'LineString', coordinates: [c[i], c[i + 1]] },
    })
  }
  return { type: 'FeatureCollection', features }
}

export function ensureRouteLayers(map: MlMap) {
  if (map.getSource(SRC)) return

  map.addSource(SRC, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  })

  // A darker casing under the line keeps it legible over any basemap colour.
  map.addLayer({
    id: LAYER_CASING,
    type: 'line',
    source: SRC,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#0f172a',
      'line-opacity': 0.25,
      'line-width': ['interpolate', ['linear'], ['zoom'], 12, 7, 18, 13],
    },
  })

  map.addLayer({
    id: LAYER_LINE,
    type: 'line',
    source: SRC,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      // Cool blue when shaded, amber in full sun (§4.3). Currently always 0.
      'line-color': [
        'interpolate',
        ['linear'],
        ['get', 'sun'],
        0,
        '#06b6d4',
        1,
        '#f59e0b',
      ],
      'line-width': ['interpolate', ['linear'], ['zoom'], 12, 4, 18, 9],
      // Behind you fades out; ahead stays solid.
      'line-opacity': ['case', ['==', ['get', 'walked'], 1], 0.3, 1],
    },
  })

  // Where you actually are on the route.
  map.addSource(HERE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
  map.addLayer({
    id: LAYER_HERE,
    type: 'circle',
    source: HERE,
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 5, 18, 9],
      'circle-color': '#ffffff',
      'circle-stroke-color': '#0e7490',
      'circle-stroke-width': 3,
    },
  })
}

/** Put the "you are here" dot on the route, or clear it. */
export function setHere(map: MlMap, point: [number, number] | null) {
  const src = map.getSource(HERE) as maplibregl.GeoJSONSource | undefined
  if (!src) return
  src.setData(
    point
      ? { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: point } }
      : { type: 'FeatureCollection', features: [] },
  )
}

export function drawRoute(
  map: MlMap,
  line: GeoJSON.LineString,
  sun: number[] = [],
  walkedUpTo = -1,
) {
  ensureRouteLayers(map)
  const src = map.getSource(SRC) as maplibregl.GeoJSONSource
  src.setData(toSegments(line, sun, walkedUpTo))
}

export function clearRoute(map: MlMap) {
  const src = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined
  src?.setData({ type: 'FeatureCollection', features: [] })
  setHere(map, null)
}

export function fitToRoute(map: MlMap, line: GeoJSON.LineString, bottomPadding: number) {
  const bounds = new maplibregl.LngLatBounds()
  for (const c of line.coordinates) bounds.extend(c as [number, number])
  map.fitBounds(bounds, {
    padding: { top: 70, left: 50, right: 50, bottom: bottomPadding + 30 },
    duration: 600,
    maxZoom: 17,
  })
}
